import { useLatestRef } from "@appretio/shared";
import type {
  CellSelectionDeleteStartEvent,
  CellValueChangedEvent,
  GridApi,
  PasteStartEvent
} from "ag-grid-enterprise";
import { useCallback, useRef } from "react";
import type { GridCellChange } from "./useWorkspaceMutationQueue";

/** The start of a burst of cell edits the grid raises one event per cell for. */
export type BatchStartEvent = PasteStartEvent | CellSelectionDeleteStartEvent;

/** One typed edit, or every cell of a paste or a range delete. */
export interface CellEditBatch {
  /** The grid that raised the edits. */
  api: GridApi;
  /** The edited cells. */
  cellChanges: GridCellChange[];
  /** The gesture, for a refusal's toast; omitted for a typed edit. */
  kind?: "paste" | "delete";
}

/** Wire shape for one edited cell. */
function toCellChange(event: CellValueChangedEvent): GridCellChange {
  return {
    rowIndex: event.rowIndex ?? undefined,
    data: {
      metric_name: event.data.metric_name,
      fiscal_year_name: event.data.fiscal_year_name,
      producer_type: event.data.producer_type
    },
    oldValue: event.oldValue,
    value: event.value,
    colId: event.column.getColId()
  };
}

/**
 * Turns a grid's cell edits into batches for `send`: a typed edit alone, and
 * a paste or a range delete as one batch of every cell it edited.
 *
 * A paste or a range delete arrives as one ``cellValueChanged`` per cell,
 * fired in a single synchronous burst. Sent one by one, a serial queue would
 * collapse them to the first and the last, so each is buffered between its
 * start and end events (DAS-6683).
 */
export function useCellEditBatcher(send: (batch: CellEditBatch) => void) {
  const sendRef = useLatestRef(send);
  // Non-null while a paste or range delete is in flight; collects its cells.
  const bufferRef = useRef<CellEditBatch | null>(null);

  const handleBatchEnd = useCallback(() => {
    const buffered = bufferRef.current;
    bufferRef.current = null;
    if (buffered?.cellChanges.length) sendRef.current(buffered);
  }, [sendRef]);

  const handleCellValueChanged = useCallback(
    (event: CellValueChangedEvent) => {
      const buffered = bufferRef.current;
      if (!buffered) {
        sendRef.current({ api: event.api, cellChanges: [toCellChange(event)] });
        return;
      }
      // Arm the rescue on the first buffered cell rather than on pasteStart:
      // AG Grid does not dispatch the cell events in the same task as
      // the start event, so a rescue queued there fires against an empty buffer
      // and disarms the batch before a single cell has arrived.
      if (buffered.cellChanges.length === 0) setTimeout(handleBatchEnd);
      buffered.cellChanges.push(toCellChange(event));
    },
    [sendRef, handleBatchEnd]
  );

  const handleBatchStart = useCallback((event: BatchStartEvent) => {
    bufferRef.current = {
      api: event.api,
      cellChanges: [],
      kind: event.type === "pasteStart" ? "paste" : "delete"
    };
  }, []);

  return { handleCellValueChanged, handleBatchStart, handleBatchEnd };
}
